import React, { useState, useEffect } from 'react';
import { MessageCircle, Users, Send, AlertCircle, Phone, X, CheckCircle, Clock, RefreshCw } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';

const BotManagementLive = () => {
  const [conversations, setConversations] = useState([]);
  const [selectedConv, setSelectedConv] = useState(null);
  const [newConvCount, setNewConvCount] = useState(0);
  const [lastConvCount, setLastConvCount] = useState(0);
  const [agents, setAgents] = useState([]);
  const [showIntervention, setShowIntervention] = useState(false);
  const [selectedAgent, setSelectedAgent] = useState(null);
  const [botSuggestion, setBotSuggestion] = useState('');
  const [showSuggestionModal, setShowSuggestionModal] = useState(false);
  const audioContextRef = React.useRef(null);
  
  const API_URL = window.location.origin;
  const token = localStorage.getItem('admin_token');

  // Créer un son spécial pour le bot (différent des autres alertes)
  const playBotNotificationSound = () => {
    try {
      if (!audioContextRef.current) {
        audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)();
      }
      
      const ctx = audioContextRef.current;
      
      // Son robotique distinctive (3 notes montantes rapides)
      [600, 800, 1000].forEach((freq, idx) => {
        setTimeout(() => {
          const oscillator = ctx.createOscillator();
          const gainNode = ctx.createGain();
          
          oscillator.connect(gainNode);
          gainNode.connect(ctx.destination);
          
          oscillator.type = 'square'; // Son robotique
          oscillator.frequency.setValueAtTime(freq, ctx.currentTime);
          
          gainNode.gain.setValueAtTime(0.3, ctx.currentTime);
          gainNode.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.15);
          
          oscillator.start(ctx.currentTime);
          oscillator.stop(ctx.currentTime + 0.15);
        }, idx * 80);
      });
      
      console.log('🤖 Son Bot joué');
    } catch (error) {
      console.error('Erreur son bot:', error);
    }
  };

  // Polling temps réel toutes les 3 secondes
  useEffect(() => {
    fetchLiveData();
    const interval = setInterval(fetchLiveData, 3000);
    return () => clearInterval(interval);
  }, []);

  const fetchLiveData = async () => {
    if (!token) return;
    
    try {
      // Conversations
      const convResp = await fetch(`${API_URL}/api/chat/conversations`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (convResp.ok) {
        const data = await convResp.json();
        
        // Filtrer conversations actives (derniers messages < 30 min)
        const now = Date.now();
        const activeConvs = data.filter(conv => {
          const lastUpdate = new Date(conv.updated_at || conv.created_at).getTime();
          return (now - lastUpdate) < 30 * 60 * 1000; // 30 minutes
        });
        
        // Compter nouvelles conversations
        const unseenConvs = activeConvs.filter(c => !c.seen_by_admin).length;
        
        // Si nouvelles conversations, jouer le son
        if (unseenConvs > lastConvCount && unseenConvs > 0) {
          playBotNotificationSound();
          console.log(`🔔 ${unseenConvs - lastConvCount} nouvelle(s) conversation(s) bot`);
        }
        
        setLastConvCount(unseenConvs);
        setNewConvCount(unseenConvs);
        setConversations(activeConvs);
      }
      
      // Agents disponibles
      const agentsResp = await fetch(`${API_URL}/api/chat/agents/available`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (agentsResp.ok) {
        const data = await agentsResp.json();
        setAgents(data.available || []);
      }
      
    } catch (error) {
      console.error('Erreur fetch live:', error);
    }
  };

  const markAsSeen = async (sessionId) => {
    try {
      await fetch(`${API_URL}/api/chat/conversations/${sessionId}/seen`, {
        method: 'POST',
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      fetchLiveData();
    } catch (error) {
      console.error('Erreur mark seen:', error);
    }
  };

  const sendIntervention = async () => {
    if (!selectedAgent || !selectedConv) return;
    
    try {
      const lastUserMessage = selectedConv.messages
        ?.filter(m => m.role === 'user')
        .pop()?.content || 'Pas de message';
      
      await fetch(`${API_URL}/api/chat/notify-staff`, {
        method: 'POST',
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          session_id: selectedConv.session_id,
          client_name: selectedConv.user_data?.name || 'Client',
          client_phone: selectedConv.user_data?.phone || 'Non fourni',
          issue: lastUserMessage.substring(0, 100),
          agent_id: selectedAgent
        })
      });
      
      alert(`✅ Agent ${selectedAgent} notifié par SMS !`);
      setShowIntervention(false);
      setSelectedAgent(null);
      
    } catch (error) {
      console.error('Erreur intervention:', error);
      alert('❌ Erreur lors de la notification');
    }
  };

  const sendSuggestionToBot = async () => {
    if (!botSuggestion.trim()) return;
    
    try {
      await fetch(`${API_URL}/api/chat/admin/suggestion`, {
        method: 'POST',
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          suggestion: botSuggestion,
          admin: localStorage.getItem('admin_username'),
          timestamp: new Date().toISOString()
        })
      });
      
      alert('✅ Suggestion enregistrée ! Le bot en tiendra compte.');
      setBotSuggestion('');
      setShowSuggestionModal(false);
      
    } catch (error) {
      console.error('Erreur suggestion:', error);
      alert('❌ Erreur lors de l\'envoi');
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 to-gray-800 p-6">
      {/* Header avec badge notification */}
      <div className="max-w-7xl mx-auto mb-6">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center space-x-4">
            <h1 className="text-3xl font-bold text-white">🤖 Gestion Bot - Live</h1>
            {newConvCount > 0 && (
              <Badge className="bg-red-500 text-white text-lg px-4 py-2 animate-pulse">
                {newConvCount} nouvelle{newConvCount > 1 ? 's' : ''} conversation{newConvCount > 1 ? 's' : ''}
              </Badge>
            )}
          </div>
          
          <div className="flex space-x-3">
            <Button
              onClick={() => setShowSuggestionModal(true)}
              className="bg-purple-600 hover:bg-purple-700"
            >
              💡 Suggérer au Bot
            </Button>
            
            <Button
              onClick={fetchLiveData}
              className="bg-blue-600 hover:bg-blue-700"
            >
              <RefreshCw size={18} className="mr-2" />
              Actualiser
            </Button>
          </div>
        </div>

        {/* Agents disponibles */}
        <Card className="mb-6 bg-gray-800 border-gray-700">
          <CardHeader>
            <CardTitle className="text-white flex items-center">
              <Users className="mr-2 text-green-400" />
              Agents disponibles maintenant ({agents.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-3">
              {agents.map(agent => (
                <div key={agent.id} className="bg-green-100 text-green-800 px-4 py-2 rounded-full font-semibold flex items-center space-x-2">
                  <span className="w-3 h-3 bg-green-500 rounded-full animate-pulse"></span>
                  <span>{agent.name}</span>
                  <span className="text-xs opacity-75">{agent.phone}</span>
                </div>
              ))}
              {agents.length === 0 && (
                <p className="text-gray-400">Aucun agent disponible actuellement</p>
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Conversations en temps réel */}
      <div className="max-w-7xl mx-auto grid md:grid-cols-2 gap-6">
        {/* Liste conversations */}
        <div className="space-y-4">
          <h2 className="text-xl font-bold text-white mb-4">
            📬 Conversations actives ({conversations.length})
          </h2>
          
          <div className="space-y-3 max-h-[600px] overflow-y-auto">
            {conversations.map(conv => (
              <Card 
                key={conv.session_id}
                className={`cursor-pointer transition hover:shadow-xl ${
                  !conv.seen_by_admin ? 'border-2 border-red-500 bg-red-50' : 'bg-white'
                } ${selectedConv?.session_id === conv.session_id ? 'ring-4 ring-blue-500' : ''}`}
                onClick={() => {
                  setSelectedConv(conv);
                  if (!conv.seen_by_admin) {
                    markAsSeen(conv.session_id);
                  }
                }}
              >
                <CardContent className="p-4">
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex items-center space-x-2">
                      <MessageCircle className={!conv.seen_by_admin ? 'text-red-500' : 'text-blue-600'} size={20} />
                      <span className="font-semibold">
                        {conv.user_data?.name || 'Client Anonyme'}
                      </span>
                      {!conv.seen_by_admin && (
                        <Badge className="bg-red-500 text-white animate-pulse">NOUVEAU</Badge>
                      )}
                      {conv.needs_agent && (
                        <Badge className="bg-orange-500 text-white">⚠️ Demande Agent</Badge>
                      )}
                    </div>
                    
                    {conv.needs_agent && (
                      <Button
                        size="sm"
                        onClick={(e) => {
                          e.stopPropagation();
                          setSelectedConv(conv);
                          setShowIntervention(true);
                        }}
                        className="bg-orange-500 hover:bg-orange-600 text-white"
                      >
                        🚨 Intervenir
                      </Button>
                    )}
                  </div>
                  
                  <p className="text-sm text-gray-600 mb-2">
                    {conv.messages?.[conv.messages.length - 1]?.content.substring(0, 80)}...
                  </p>
                  
                  <div className="flex items-center space-x-3 text-xs text-gray-500">
                    <span>💬 {conv.messages?.length || 0} messages</span>
                    <span className="flex items-center">
                      <Clock size={12} className="mr-1" />
                      {new Date(conv.updated_at || conv.created_at).toLocaleTimeString('fr-FR')}
                    </span>
                    {conv.user_data?.phone && (
                      <span>📞 {conv.user_data.phone}</span>
                    )}
                  </div>
                </CardContent>
              </Card>
            ))}
            
            {conversations.length === 0 && (
              <Card className="bg-gray-100">
                <CardContent className="p-8 text-center">
                  <MessageCircle size={48} className="mx-auto mb-4 text-gray-400" />
                  <p className="text-gray-600">Aucune conversation active</p>
                </CardContent>
              </Card>
            )}
          </div>
        </div>

        {/* Détail conversation sélectionnée */}
        <div>
          {selectedConv ? (
            <Card className="bg-white sticky top-6">
              <CardHeader className="bg-gradient-to-r from-blue-600 to-purple-600 text-white">
                <CardTitle className="flex items-center justify-between">
                  <span>Conversation en cours</span>
                  <Button
                    size="sm"
                    onClick={() => setShowIntervention(true)}
                    className="bg-orange-500 hover:bg-orange-600"
                  >
                    🚨 Intervenir
                  </Button>
                </CardTitle>
              </CardHeader>
              <CardContent className="p-4">
                <div className="mb-4 p-3 bg-gray-100 rounded">
                  <p className="text-sm font-semibold mb-1">Client: {selectedConv.user_data?.name || 'Anonyme'}</p>
                  {selectedConv.user_data?.phone && (
                    <p className="text-sm">📞 {selectedConv.user_data.phone}</p>
                  )}
                  {selectedConv.user_data?.email && (
                    <p className="text-sm">📧 {selectedConv.user_data.email}</p>
                  )}
                </div>
                
                <div className="space-y-3 max-h-[400px] overflow-y-auto">
                  {selectedConv.messages?.map((msg, idx) => (
                    <div
                      key={idx}
                      className={`p-3 rounded-lg ${
                        msg.role === 'user'
                          ? 'bg-blue-100 ml-8'
                          : 'bg-gray-100 mr-8'
                      }`}
                    >
                      <p className="text-xs font-semibold mb-1 text-gray-600">
                        {msg.role === 'user' ? '👤 Client' : '🤖 Sophie'}
                      </p>
                      <p className="text-sm whitespace-pre-wrap">{msg.content}</p>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          ) : (
            <Card className="bg-gray-100">
              <CardContent className="p-12 text-center">
                <MessageCircle size={64} className="mx-auto mb-4 text-gray-400" />
                <p className="text-gray-600">Sélectionnez une conversation pour voir les détails</p>
              </CardContent>
            </Card>
          )}
        </div>
      </div>

      {/* Modal Intervention */}
      {showIntervention && selectedConv && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <Card className="w-full max-w-md">
            <CardHeader className="bg-orange-500 text-white">
              <CardTitle className="flex items-center justify-between">
                <span>🚨 Intervention Agent</span>
                <button onClick={() => setShowIntervention(false)}>
                  <X size={24} />
                </button>
              </CardTitle>
            </CardHeader>
            <CardContent className="p-6">
              <p className="mb-4 text-gray-700">
                Choisissez un agent à notifier par SMS pour cette conversation :
              </p>
              
              <div className="space-y-3 mb-6">
                {agents.map(agent => (
                  <button
                    key={agent.id}
                    onClick={() => setSelectedAgent(agent.id)}
                    className={`w-full p-4 rounded-lg border-2 transition ${
                      selectedAgent === agent.id
                        ? 'border-orange-500 bg-orange-50'
                        : 'border-gray-300 hover:border-orange-300'
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-semibold">{agent.name}</p>
                        <p className="text-sm text-gray-600">{agent.phone}</p>
                      </div>
                      {selectedAgent === agent.id && (
                        <CheckCircle className="text-orange-500" size={24} />
                      )}
                    </div>
                  </button>
                ))}
                
                {agents.length === 0 && (
                  <p className="text-gray-500 text-center">Aucun agent disponible actuellement</p>
                )}
              </div>
              
              <div className="flex space-x-3">
                <Button
                  onClick={() => setShowIntervention(false)}
                  className="flex-1 bg-gray-500 hover:bg-gray-600"
                >
                  Annuler
                </Button>
                <Button
                  onClick={sendIntervention}
                  disabled={!selectedAgent}
                  className="flex-1 bg-orange-500 hover:bg-orange-600 disabled:opacity-50"
                >
                  📤 Notifier Agent
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Modal Suggestion Bot */}
      {showSuggestionModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <Card className="w-full max-w-2xl">
            <CardHeader className="bg-purple-600 text-white">
              <CardTitle className="flex items-center justify-between">
                <span>💡 Suggérer des infos au Bot</span>
                <button onClick={() => setShowSuggestionModal(false)}>
                  <X size={24} />
                </button>
              </CardTitle>
            </CardHeader>
            <CardContent className="p-6">
              <p className="mb-4 text-gray-700">
                Donnez des instructions ou suggestions au bot pour améliorer ses réponses :
              </p>
              
              <textarea
                value={botSuggestion}
                onChange={(e) => setBotSuggestion(e.target.value)}
                placeholder="Ex: Rappelle aux clients qu'on fait aussi la réparation de consoles de jeux. Mentionne qu'on a un service de récupération à domicile."
                className="w-full h-40 p-4 border-2 border-gray-300 rounded-lg focus:border-purple-500 focus:ring-2 focus:ring-purple-200"
              />
              
              <div className="flex space-x-3 mt-4">
                <Button
                  onClick={() => setShowSuggestionModal(false)}
                  className="flex-1 bg-gray-500 hover:bg-gray-600"
                >
                  Annuler
                </Button>
                <Button
                  onClick={sendSuggestionToBot}
                  disabled={!botSuggestion.trim()}
                  className="flex-1 bg-purple-600 hover:bg-purple-700 disabled:opacity-50"
                >
                  📤 Envoyer Suggestion
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};

export default BotManagementLive;
